import {
  alpha,
  Box,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { amazonColors } from "../../../config/amazonTheme";
import { UserAddress } from "../../../types/userAddress";

import AddressSelector from "./AddressSelector";
import { DeliveryInfo } from "./types";

interface DeliveryInformationProps {
  deliveryInfo: DeliveryInfo;
  isEditing: boolean;
  onDeliveryInfoChange: (field: keyof DeliveryInfo, value: string) => void;
  onToggleEdit: () => void;
  mobileSidebar?: React.ReactNode;
}

const DeliveryInformation: React.FC<DeliveryInformationProps> = ({
  deliveryInfo,
  onDeliveryInfoChange,
  mobileSidebar,
}) => {
  const { t: myTranslate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleAddressSelect = useCallback(
    (addressId: string) => {
      onDeliveryInfoChange("addressId", addressId);
    },
    [onDeliveryInfoChange]
  );

  const handleAddressChange = useCallback(
    (address: UserAddress) => {
      onDeliveryInfoChange("addressId", address.id);
      onDeliveryInfoChange("address", address.addressLine1);
      onDeliveryInfoChange("addressLine2", address.addressLine2 || "");
      onDeliveryInfoChange("city", address.city);
      onDeliveryInfoChange("region", address.region || "");
      onDeliveryInfoChange("zipCode", address.postalCode || "");
      onDeliveryInfoChange("country", address.country);
      onDeliveryInfoChange("phone", address.phoneNumber);
      onDeliveryInfoChange(
        "deliveryInstructions",
        address.deliveryInstructions || ""
      );
    },
    [onDeliveryInfoChange]
  );

  const handlePickupLocationChange = (
    event: React.MouseEvent<HTMLElement>,
    newLocation: string | null
  ) => {
    if (newLocation !== null) {
      onDeliveryInfoChange("pickupLocation", newLocation);
    }
  };

  return (
    <Box>
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
          overflow: "visible",
        }}
        className="notranslate"
        translate="no"
      >
        <CardContent sx={{ p: { xs: 3, sm: 3 } }}>
          {/* Compact Pickup Selector (Segmented Control style) */}
          <Box mb={3}>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              mb={1}
              textTransform="uppercase"
              letterSpacing={0.5}
            >
              {myTranslate("checkout.selectPickupLocation")}
            </Typography>

            <ToggleButtonGroup
              value={deliveryInfo.pickupLocation || "Dushanbe"}
              exclusive
              onChange={handlePickupLocationChange}
              fullWidth
              size="small"
              sx={{
                height: 40,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  color: "text.secondary",
                  borderColor: theme.palette.divider,
                  "&.Mui-selected": {
                    bgcolor: alpha(amazonColors.blue, 0.08),
                    color: amazonColors.blue,
                    borderColor: amazonColors.blue,
                    "&:hover": {
                      bgcolor: alpha(amazonColors.blue, 0.12),
                    },
                  },
                },
              }}
            >
              <ToggleButton value="Dushanbe">
                {myTranslate("checkout.dushanbe")}
              </ToggleButton>
              <ToggleButton value="Khujand">
                {myTranslate("checkout.khujand")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Address Selection Area */}
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              display="block"
              mb={1}
              textTransform="uppercase"
              letterSpacing={0.5}
            >
              {myTranslate("checkout.deliveryInformation")}
            </Typography>

            <AddressSelector
              selectedAddressId={deliveryInfo.addressId}
              onAddressSelect={handleAddressSelect}
              onAddressChange={handleAddressChange}
              showHeader={false}
            />
          </Box>
        </CardContent>
      </Card>

      {isMobile && mobileSidebar && <Box sx={{ mt: 2 }}>{mobileSidebar}</Box>}
    </Box>
  );
};

export default DeliveryInformation;
